<div class="vr-hist">

  <!-- A div, not a <button>: the header carries its own Playback button, and
       nesting buttons is invalid HTML. -->
  <div class="vr-hist__head"
       role="button"
       tabindex="0"
       [attr.aria-expanded]="expanded"
       (click)="toggle()"
       (keydown.enter)="toggle()"
       (keydown.space)="$event.preventDefault(); toggle()">
    <mat-icon class="material-icons-outlined vr-hist__icon">history</mat-icon>
    <span class="vr-hist__title">Refresh History</span>
    <span class="vr-hist__count">{{ count }}</span>
    <span class="vr-hist__spacer"></span>

    <!-- Archive-scope action in the archive's header. Withheld below two
         snapshots, where there is no timeline to play, and withheld outright on
         a POINT task — see `canPlayback`: a nearby-ranked archive has no area
         whose traffic a flip-book could replay.

         Three states, not two: a replay of a large archive is dozens of signed
         URLs and as many storage reads, and the pill used to sit unchanged
         through all of it while the ON MAP bar claimed the sensor was being
         REFRESHED. The spinner is bound to the DELAYED flag, so a window still
         in the step cache re-enters without one. -->
    <button type="button" class="vr-hist__play"
            *ngIf="canPlayback"
            [class.is-loading]="playbackLoading"
            [disabled]="locked"
            [matTooltip]="playTooltip"
            matTooltipPosition="right" matTooltipClass="vr-tip"
            (click)="$event.stopPropagation(); playAll.emit()">
      <mat-icon class="material-icons-outlined"
                [class.vr-hist__spin]="playbackLoading">{{ playbackLoading ? 'autorenew' : 'play_circle' }}</mat-icon>
      {{ playLabel }}
    </button>

    <mat-icon class="vr-hist__caret">{{ expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
  </div>

  <ng-container *ngIf="expanded">

    <!-- Never refreshed. Rendered rather than hidden: an empty section explains
         what refreshing will build, where a missing one explains nothing. -->
    <div class="vr-hist__none" *ngIf="!count">
      <mat-icon class="material-icons-outlined">history_toggle_off</mat-icon>
      <div>
        <div class="vr-hist__none-title">No refreshes yet</div>
        <div class="vr-hist__none-note">
          Each refresh is saved here so you can revisit it later.
        </div>
      </div>
    </div>

    <div class="vr-hist__list" *ngIf="count"
         role="radiogroup" aria-label="Recent refresh history snapshots">
      <ng-container *ngFor="let group of groups">
        <div class="vr-hist__day">{{ group.label }}</div>
        <div class="vr-hist__rows">
          <ng-container *ngFor="let row of group.rows">

            <!-- A div, not a button: the row carries its own <button> for download,
                 and nesting buttons is invalid HTML.

                 `is-playing` is the frame the replay is on right now — the list
                 following playback the way the Vessel Tracks list follows the
                 drawn track. Deliberately a different mark from `is-active`: a
                 frame flicking past is not a snapshot the user chose, and the two
                 can be true of different rows at once (a replay runs over
                 whatever was on the map before it).

                 `is-locked` withholds the pick while a replay owns the map; see
                 the `locked` note on the component. -->
            <div class="vr-hist__snap" role="radio"
                 [attr.tabindex]="locked ? -1 : 0"
                 [class.is-active]="isActive(row.refreshId)"
                 [class.is-playing]="isPlayingRow(row.refreshId)"
                 [class.is-locked]="locked"
                 [class.is-flash]="row.refreshId === flashRefreshId"
                 [attr.aria-checked]="isActive(row.refreshId)"
                 [attr.aria-disabled]="locked"
                 [matTooltip]="rowTooltip(row)"
                 matTooltipPosition="right" matTooltipClass="vr-tip"
                 (click)="pick(row)"
                 (keydown.enter)="pick(row)"
                 (keydown.space)="$event.preventDefault(); pick(row)">
              <span class="vr-hist__dot"></span>
              <span class="vr-hist__time">{{ row.time }}</span>
              <span class="vr-hist__z">UTC</span>
              <!-- Replaces LATEST while this row is the frame on screen: both are
                   one-per-list chips in the same slot, and the badge that says
                   where the replay IS outranks the one that says where the
                   archive ends.

                   "ON MAP" rather than "PLAYING" because it stays true when the
                   transport does not: a replay pauses, is scrubbed and stops on
                   its last frame, and through all of that this is still the
                   snapshot the map is showing. The word costs nothing to keep
                   honest and it is the only part of this mark a screen reader
                   can reach — the rail, the fill and the pulse beside it are
                   colour, which cannot carry the fact on its own. -->
              <span class="vr-hist__badge is-playing" *ngIf="isPlayingRow(row.refreshId)">ON MAP</span>
              <span class="vr-hist__badge" *ngIf="row.isLatest && !isPlayingRow(row.refreshId)">LATEST</span>
              <!-- The day header already carries the date and the tooltip carries the
                   age, so the row's spare width goes to the one thing neither of them
                   says: how many vessels. -->
              <span class="vr-hist__end">
                <span class="vr-hist__vessels" *ngIf="row.count != null">{{ row.count }}</span>
              </span>
              <button type="button" class="vr-hist__dl"
                      matTooltip="Download this snapshot"
                      matTooltipPosition="right" matTooltipClass="vr-tip"
                      (click)="$event.stopPropagation(); download.emit(row.data)">
                <mat-icon class="material-icons-outlined">file_download</mat-icon>
              </button>
            </div>

          </ng-container>
        </div>
      </ng-container>
    </div>

    <!-- The handoff to the archive. Carries the total and the coverage span, so
         the panel states how much history exists without having to render it. -->
    <button type="button" class="vr-hist__browse" *ngIf="count" (click)="browseAll.emit()">
      <mat-icon class="material-icons-outlined">calendar_month</mat-icon>
      <span class="vr-hist__browse-txt">{{ browseLabel }}</span>
      <mat-icon class="vr-hist__browse-go">arrow_forward</mat-icon>
    </button>

  </ng-container>

</div>
